import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { buildSchema, canonicalFor, SOCIAL_IMAGE_PATH, normalizePath, routeSeo, SITE_NAME, SITE_ORIGIN, SOCIAL_IMAGE_ALT, SOCIAL_IMAGE_HEIGHT, SOCIAL_IMAGE_WIDTH } from "../seo";

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value));
}

export function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = normalizePath(pathname);
    const seo = routeSeo[normalizedPath];
    const knownRoute = Boolean(seo);
    const noindex = document.documentElement.dataset.noindex === "true" || ["localhost", "127.0.0.1"].includes(window.location.hostname);
    const resolved = seo ?? { title: "Page Not Found | Nexus Supply Chain", description: "The requested Nexus Supply Chain page could not be found." };
    const canonicalUrl = canonicalFor(knownRoute ? normalizedPath : "/");
    const logoUrl = `${SITE_ORIGIN}${SOCIAL_IMAGE_PATH}`;

    document.documentElement.lang = "en";
    document.title = resolved.title;
    upsertMeta('meta[name="description"]', { name: "description", content: resolved.description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: knownRoute && !noindex ? "index, follow" : "noindex, nofollow" });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_US" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: resolved.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: resolved.description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: logoUrl });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: SOCIAL_IMAGE_WIDTH });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: SOCIAL_IMAGE_HEIGHT });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: SOCIAL_IMAGE_ALT });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: resolved.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: resolved.description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: logoUrl });

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    let schema = document.getElementById("nexus-route-schema") as HTMLScriptElement | null;
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "nexus-route-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify(buildSchema(normalizedPath, resolved));
  }, [pathname]);

  return null;
}
